import { currentLocale, requestLocale } from "../lib/i18n";
import {
  canonicalStockyPublicBigIntCursor,
  canonicalStockyPublicBigIntId,
  encodeStockySourceArchiveId,
  requireStockySourceArchiveId,
  type StockySourceArchiveId,
} from "./stocky_public_ids";
import {
  ApiError,
  ShopGenerationChangedError,
  assertCurrentShopGeneration,
  apiFetch,
  captureActiveTenantShopGeneration,
  normalizeErrorDetail,
  selfHealOAuthInstall,
  shopBoundAuthHeader,
} from "./client";

export interface StockySourceArchive {
  id: string;
  filename: string;
  category: string;
  sha256: string;
  size_bytes: number;
  row_count: number | null;
  context: string | null;
  generic_chunk_available: boolean;
  generic_chunk_unavailable_reason: string | null;
  parse_options: {
    version: 1;
    delimiter: "auto" | "comma" | "tab" | "semicolon" | "pipe";
    resolved_delimiter: "comma" | "tab" | "semicolon" | "pipe" | null;
    header_row: number;
    column_mappings: Record<string, string>;
  };
  created_at: string;
}

export interface StockySourceArchiveLimits {
  max_files_per_request: number;
  max_request_bytes: number;
  max_file_bytes: number;
  max_stored_file_bytes: number;
  max_shop_bytes: number;
  max_shop_stored_bytes: number;
  max_files_per_shop: number;
  max_portability_zip_bytes: number;
  max_portability_zip_source_files: number;
  max_portability_dataset_chunk_bytes: number;
  max_portability_dataset_chunk_rows: number;
  max_portability_generic_download_bytes: number;
}

export interface StockySourceArchiveList {
  archives: StockySourceArchive[];
  total: number;
  next_cursor: string | null;
  limits: StockySourceArchiveLimits;
  usage: StockySourceArchiveUsage;
}

export interface StockySourceArchiveUsage {
  used_raw_bytes: number;
  remaining_raw_bytes: number;
  file_count: number;
  remaining_files: number;
}

export interface StockySourceArchiveUpload extends StockySourceArchive {
  already_archived: boolean;
  duplicate_content: boolean;
  duplicate_archive_ids: string[];
  duplicate_count: number;
  duplicate_first_archive_id: string | null;
  duplicate_last_archive_id: string | null;
}

export interface StockyArchiveOccurrence {
  file: File;
  sourceImportId: string;
}

export interface StockyTextOccurrence {
  text: string;
  sourceImportId: string;
}

export interface StockyGenericParseOptions {
  version: 1;
  delimiter: "auto" | "comma" | "tab" | "semicolon" | "pipe";
  header_row: number;
  column_mappings: Record<string, string>;
}

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

export interface StockyPortabilityDataset {
  dataset: string;
  key: string;
  columns: string[];
  primary_format: "canonical_jsonl";
  convenience_format: "formula_safe_csv";
  max_chunk_rows: number;
  max_chunk_bytes: number;
  completion_requires_verification: true;
}

export interface StockyPortabilityDatasetList {
  datasets: StockyPortabilityDataset[];
  completion_contract: string;
}

export interface DownloadedDatasetChunk extends DownloadedFile {
  dataset: string;
  recordCount: number;
  cumulativeRecordCount: number;
  highWatermark: string;
  partSequence: number;
  expiresAt: number;
  nextCursor: string;
  completeCandidate: boolean;
  orderedRoot: string;
}

export interface DownloadedGenericRowChunk extends DownloadedFile {
  archiveId: string;
  sourceSha256: string;
  recordCount: number;
  cumulativeRecordCount: number;
  highWatermark: number;
  partSequence: number;
  expiresAt: number;
  nextCursor: string;
  completeCandidate: boolean;
  orderedRoot: string;
}

export interface StockyPortabilityDatasetVerification {
  dataset: string;
  high_watermark: string;
  record_count: number;
  ordered_root: string;
  verified: true;
  newer_ids_excluded: true;
}

export interface StockyGenericRowVerification {
  archive_id: string;
  source_sha256: string;
  high_watermark: number;
  record_count: number;
  part_count: number;
  ordered_root: string;
  verified: true;
}

export const MAX_STOCKY_PORTABILITY_ZIP_BYTES = 64 * 1024 * 1024;
export const MAX_STOCKY_DATASET_CHUNK_BYTES = 8 * 1024 * 1024;
export const MAX_STOCKY_GENERIC_DOWNLOAD_BYTES = 9 * 1024 * 1024;
export const MAX_STOCKY_GENERIC_CHUNK_BYTES = 8 * 1024 * 1024;
export const STOCKY_PORTABILITY_CURSOR_TTL_SECONDS = 24 * 60 * 60;

const PORTABILITY = "/api/migrate/portability";

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? "";
}

async function rawHeaders(
  expectedShopDomain: string,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "X-UreyukiBox-Locale": requestLocale(),
    "Accept-Language": requestLocale(),
    ...(await shopBoundAuthHeader(expectedShopDomain)),
  };
  return headers;
}

const rawResponseShopGeneration = new WeakMap<Response, string>();

/**
 * Integrity failures detected in this browser client have Japanese source
 * details for the JA UI. Server-provided `ApiError`s already arrive in the
 * request locale and must pass through unchanged; only use this helper for
 * checks performed locally after a response has been accepted.
 */
export function localPortabilityIntegrityError(jaDetail: string): ApiError {
  return new ApiError(
    502,
    currentLocale() !== "ja"
      ? "We could not verify the saved-data download. No saved data was changed. Try again."
      : jaDetail,
  );
}

function assertRawResponseShopGeneration(response: Response): void {
  const expected = rawResponseShopGeneration.get(response);
  if (expected === undefined) throw new ShopGenerationChangedError();
  assertCurrentShopGeneration(expected);
}

async function checkedRawFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const expectedShopDomain = captureActiveTenantShopGeneration();
  const response = await fetch(apiBaseUrl() + path, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      ...(await rawHeaders(expectedShopDomain)),
    },
  });
  assertCurrentShopGeneration(expectedShopDomain);
  if (!response.ok) {
    const detail = normalizeErrorDetail(await response.text());
    assertCurrentShopGeneration(expectedShopDomain);
    selfHealOAuthInstall(response.status, detail);
    const retryAfter = response.headers.get("Retry-After");
    throw new ApiError(
      response.status,
      (response.status === 429 || response.status === 507) && retryAfter
        ? currentLocale() !== "ja"
          ? `${detail} (retry after approximately ${retryAfter} seconds)`
          : `${detail}（再試行の目安: ${retryAfter}秒後）`
        : detail,
    );
  }
  rawResponseShopGeneration.set(response, expectedShopDomain);
  return response;
}

function requiredBoundedLength(response: Response, maximum: number): number {
  const raw = response.headers.get("Content-Length");
  const length = raw === null ? Number.NaN : Number(raw);
  if (!Number.isSafeInteger(length) || length < 0 || length > maximum) {
    throw localPortabilityIntegrityError(
      "ダウンロードサイズを安全に確認できませんでした。時間をおいて再試行してください。",
    );
  }
  return length;
}

function requiredContentSha256(response: Response): string {
  const value = response.headers.get("X-Content-SHA256") ?? "";
  if (!/^[a-f0-9]{64}$/.test(value)) {
    throw localPortabilityIntegrityError("ダウンロードのSHA-256ヘッダーを確認できませんでした。");
  }
  return value;
}

async function boundedResponseBlob(
  response: Response,
  maximum: number,
  expectedSha256?: string,
): Promise<Blob> {
  assertRawResponseShopGeneration(response);
  const expected = requiredBoundedLength(response, maximum);
  if (response.body === null) {
    const bytes = await response.arrayBuffer();
    assertRawResponseShopGeneration(response);
    if (bytes.byteLength !== expected || bytes.byteLength > maximum) {
      throw localPortabilityIntegrityError("ダウンロードしたデータのサイズが一致しません。");
    }
    if (expectedSha256) {
      await verifyBytesSha256([bytes], bytes.byteLength, expectedSha256);
      assertRawResponseShopGeneration(response);
    }
    return new Blob([bytes], {
      type: response.headers.get("Content-Type") ?? "application/octet-stream",
    });
  }
  const reader = response.body.getReader();
  const chunks: ArrayBuffer[] = [];
  let total = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      assertRawResponseShopGeneration(response);
      if (done) break;
      total += value.byteLength;
      if (total > expected || total > maximum) {
        await reader.cancel("bounded download exceeded declared size");
        throw localPortabilityIntegrityError("ダウンロードしたデータが上限を超えました。");
      }
      const copy = new Uint8Array(value.byteLength);
      copy.set(value);
      chunks.push(copy.buffer);
    }
  } catch (error) {
    try {
      await reader.cancel("bounded download did not complete");
    } catch {
      // Preserve the primary generation/network/integrity error.
    }
    throw error;
  } finally {
    reader.releaseLock();
  }
  if (total !== expected) {
    throw localPortabilityIntegrityError("ダウンロードしたデータのサイズが一致しません。");
  }
  if (expectedSha256) {
    await verifyBytesSha256(chunks, total, expectedSha256);
    assertRawResponseShopGeneration(response);
  }
  assertRawResponseShopGeneration(response);
  return new Blob(chunks, {
    type: response.headers.get("Content-Type") ?? "application/octet-stream",
  });
}

async function verifyBytesSha256(
  chunks: ArrayBuffer[],
  total: number,
  expected: string,
): Promise<void> {
  if (!/^[a-f0-9]{64}$/.test(expected)) {
    throw localPortabilityIntegrityError("ダウンロードのSHA-256を確認できませんでした。");
  }
  const contiguous = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    contiguous.set(new Uint8Array(chunk), offset);
    offset += chunk.byteLength;
  }
  const digest = await globalThis.crypto.subtle.digest("SHA-256", contiguous);
  const actual = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  if (actual !== expected) {
    throw localPortabilityIntegrityError("ダウンロードした原本のSHA-256が一致しません。");
  }
}

function responseFilename(response: Response, fallback: string): string {
  const value = response.headers.get("Content-Disposition") ?? "";
  const encoded = value.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return fallback;
    }
  }
  return (
    value
      .match(/filename=(?:"([^"]+)"|([^;\s]+))/i)
      ?.slice(1)
      .find(Boolean) ?? fallback
  );
}

/** Download a fully spooled, size-bounded and SHA-verified generated file. */
export async function downloadVerifiedBoundedFile(
  path: string,
  input: {
    maximumBytes: number;
    fallbackFilename: string;
    expectedShopDomain: string;
  },
): Promise<DownloadedFile> {
  assertCurrentShopGeneration(input.expectedShopDomain);
  const response = await checkedRawFetch(path);
  assertCurrentShopGeneration(input.expectedShopDomain);
  const contentSha256 = requiredContentSha256(response);
  const blob = await boundedResponseBlob(
    response,
    input.maximumBytes,
    contentSha256,
  );
  assertCurrentShopGeneration(input.expectedShopDomain);
  return {
    blob,
    filename: responseFilename(response, input.fallbackFilename),
  };
}

function canonicalArchiveResponseId(value: unknown): string {
  try {
    return canonicalStockyPublicBigIntId(value);
  } catch {
    throw localPortabilityIntegrityError("保存済み原本の識別子を確認できませんでした。");
  }
}

function normalizeArchiveMetadataIds(
  archive: StockySourceArchive & { import_context?: unknown },
): StockySourceArchive {
  const legacyContext =
    archive.import_context !== null &&
    typeof archive.import_context === "object" &&
    !Array.isArray(archive.import_context)
      ? (archive.import_context as Record<string, unknown>).context
      : null;
  return {
    id: canonicalArchiveResponseId(archive.id),
    filename: archive.filename,
    category: archive.category,
    sha256: archive.sha256,
    size_bytes: archive.size_bytes,
    row_count: archive.row_count,
    context:
      typeof archive.context === "string"
        ? archive.context
        : typeof legacyContext === "string"
          ? legacyContext
          : null,
    generic_chunk_available: archive.generic_chunk_available,
    generic_chunk_unavailable_reason:
      archive.generic_chunk_unavailable_reason,
    parse_options: archive.parse_options,
    created_at: archive.created_at,
  };
}

function normalizeArchiveUploadIds(
  archive: StockySourceArchiveUpload,
): StockySourceArchiveUpload {
  return {
    ...normalizeArchiveMetadataIds(archive),
    already_archived: archive.already_archived,
    duplicate_content: archive.duplicate_content,
    duplicate_count: archive.duplicate_count,
    duplicate_archive_ids: (archive.duplicate_archive_ids ?? []).map(
      canonicalArchiveResponseId,
    ),
    duplicate_first_archive_id:
      archive.duplicate_first_archive_id == null
        ? null
        : canonicalArchiveResponseId(archive.duplicate_first_archive_id),
    duplicate_last_archive_id:
      archive.duplicate_last_archive_id == null
        ? null
        : canonicalArchiveResponseId(archive.duplicate_last_archive_id),
  };
}

export async function listStockySourceArchives(
  cursor?: StockySourceArchiveId,
): Promise<StockySourceArchiveList> {
  const validatedCursor =
    cursor === undefined ? undefined : requireStockySourceArchiveId(cursor);
  const result = await apiFetch<StockySourceArchiveList>(
    `${PORTABILITY}/sources`,
    { query: { limit: 100, cursor: validatedCursor } },
  );
  return {
    ...result,
    archives: result.archives.map(normalizeArchiveMetadataIds),
    next_cursor:
      result.next_cursor === null
        ? null
        : canonicalArchiveResponseId(result.next_cursor),
  };
}

export async function uploadStockySourceArchive(input: {
  occurrence: StockyArchiveOccurrence;
  category: string;
  context: string;
  parseOptions?: StockyGenericParseOptions | null;
  archiveOnly: boolean;
}): Promise<StockySourceArchiveUpload> {
  const form = new FormData();
  form.set("file", input.occurrence.file);
  form.set("category", input.category);
  form.set("source_import_id", input.occurrence.sourceImportId);
  form.set("import_context", JSON.stringify({ context: input.context }));
  if (input.parseOptions) {
    form.set("parse_options", JSON.stringify(input.parseOptions));
  }
  form.set("archive_only", String(input.archiveOnly));
  const response = await checkedRawFetch(`${PORTABILITY}/sources`, {
    method: "POST",
    body: form,
  });
  const uploaded = (await response.json()) as StockySourceArchiveUpload;
  assertRawResponseShopGeneration(response);
  return normalizeArchiveUploadIds(uploaded);
}

export async function uploadStockyTextArchive(input: {
  occurrence: StockyTextOccurrence;
  category: string;
  sourceFilename: string;
  context: string;
  mediaType?: string;
  parseOptions?: StockyGenericParseOptions | null;
  archiveOnly: boolean;
}): Promise<StockySourceArchiveUpload> {
  const uploaded = await apiFetch<StockySourceArchiveUpload>(
    `${PORTABILITY}/sources/text`,
    {
      method: "POST",
      body: {
        source_import_id: input.occurrence.sourceImportId,
        category: input.category,
        source_filename: input.sourceFilename,
        text: input.occurrence.text,
        import_context: { context: input.context },
        media_type: input.mediaType ?? "text/plain",
        parse_options: input.parseOptions ?? null,
        archive_only: input.archiveOnly,
      },
    },
  );
  return normalizeArchiveUploadIds(uploaded);
}

export async function downloadStockySourceArchive(
  archive: Pick<StockySourceArchive, "filename" | "size_bytes" | "sha256"> & {
    id: StockySourceArchiveId;
    links?: { download: string };
  },
): Promise<DownloadedFile> {
  const archiveId = requireStockySourceArchiveId(archive.id);
  const expectedPath = `${PORTABILITY}/sources/${encodeStockySourceArchiveId(archiveId)}/download`;
  const downloadPath = archive.links?.download ?? expectedPath;
  if (downloadPath !== expectedPath) {
    throw localPortabilityIntegrityError("保存済み原本のダウンロード先を確認できませんでした。");
  }
  const response = await checkedRawFetch(downloadPath);
  if (
    response.headers.get("X-Source-Archive-ID") !== String(archiveId) ||
    response.headers.get("X-Source-SHA256") !== archive.sha256 ||
    response.headers.get("X-Content-SHA256") !== archive.sha256
  ) {
    throw localPortabilityIntegrityError("ダウンロード原本と一覧情報の対応を確認できませんでした。");
  }
  const blob = await boundedResponseBlob(
    response,
    archive.size_bytes,
    archive.sha256,
  );
  return {
    blob,
    filename: responseFilename(response, archive.filename),
  };
}

export async function downloadStockySourceGenericRows(
  archive: Pick<StockySourceArchive, "sha256"> & {
    id: StockySourceArchiveId;
  },
): Promise<DownloadedFile> {
  const archiveId = requireStockySourceArchiveId(archive.id);
  const response = await checkedRawFetch(
    `${PORTABILITY}/sources/${encodeStockySourceArchiveId(archiveId)}/generic-rows/download`,
  );
  if (
    response.headers.get("X-Source-Archive-ID") !== String(archiveId) ||
    response.headers.get("X-Source-SHA256") !== archive.sha256
  ) {
    throw localPortabilityIntegrityError("汎用行と原本の対応を確認できませんでした。");
  }
  const contentSha256 = requiredContentSha256(response);
  return {
    blob: await boundedResponseBlob(
      response,
      MAX_STOCKY_GENERIC_DOWNLOAD_BYTES,
      contentSha256,
    ),
    filename: responseFilename(
      response,
      `ureyukibox-stocky-generic-${archive.id}.zip`,
    ),
  };
}

export async function downloadStockyPortabilityZip(): Promise<DownloadedFile> {
  const response = await checkedRawFetch(`${PORTABILITY}/export`);
  const contentSha256 = requiredContentSha256(response);
  return {
    blob: await boundedResponseBlob(
      response,
      MAX_STOCKY_PORTABILITY_ZIP_BYTES,
      contentSha256,
    ),
    filename: responseFilename(response, "ureyukibox-stocky-export.zip"),
  };
}

export async function listStockyPortabilityDatasets(): Promise<StockyPortabilityDatasetList> {
  const result = await apiFetch<StockyPortabilityDatasetList>(
    `${PORTABILITY}/datasets`,
  );
  return {
    completion_contract: result.completion_contract,
    datasets: result.datasets.map((dataset) => ({
      dataset: dataset.dataset,
      key: dataset.key,
      columns: dataset.columns,
      primary_format: dataset.primary_format,
      convenience_format: dataset.convenience_format,
      max_chunk_rows: dataset.max_chunk_rows,
      max_chunk_bytes: dataset.max_chunk_bytes,
      completion_requires_verification:
        dataset.completion_requires_verification,
    })),
  };
}

function requiredIntegerHeader(response: Response, name: string): number {
  const value = Number(response.headers.get(name));
  if (!Number.isSafeInteger(value) || value < 0) {
    throw localPortabilityIntegrityError(`データセットの ${name} を確認できません。`);
  }
  return value;
}

function requiredPublicBigIntCursorHeader(
  response: Response,
  name: string,
): string {
  try {
    return canonicalStockyPublicBigIntCursor(response.headers.get(name));
  } catch {
    throw localPortabilityIntegrityError(`データセットの ${name} を確認できません。`);
  }
}

function requiredPositiveIntegerHeader(
  response: Response,
  name: string,
): number {
  const value = requiredIntegerHeader(response, name);
  if (value < 1) {
    throw localPortabilityIntegrityError(`データセットの ${name} を確認できません。`);
  }
  return value;
}

function requiredFinalHeader(response: Response): boolean {
  const value = response.headers.get("X-Chunk-Final");
  if (value !== "true" && value !== "false") {
    throw localPortabilityIntegrityError("データセットの完了候補を確認できません。");
  }
  return value === "true";
}

export async function downloadStockyPortabilityDatasetChunk(
  dataset: string,
  cursor: string | null,
): Promise<DownloadedDatasetChunk> {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  const response = await checkedRawFetch(
    `${PORTABILITY}/datasets/${encodeURIComponent(dataset)}/chunks${query}`,
  );
  const responseDataset = response.headers.get("X-Dataset") ?? "";
  const nextCursor = response.headers.get("X-Next-Cursor") ?? "";
  const orderedRoot = response.headers.get("X-Ordered-Root") ?? "";
  const contentSha256 = requiredContentSha256(response);
  if (
    responseDataset !== dataset ||
    nextCursor.length === 0 ||
    !/^[a-f0-9]{64}$/.test(orderedRoot)
  ) {
    throw localPortabilityIntegrityError("データセットの継続情報を確認できません。");
  }
  const recordCount = requiredIntegerHeader(response, "X-Record-Count");
  const cumulativeRecordCount = requiredIntegerHeader(
    response,
    "X-Cumulative-Record-Count",
  );
  const expiresAt = requiredPositiveIntegerHeader(
    response,
    "X-Cursor-Expires-At",
  );
  const now = Math.floor(Date.now() / 1000);
  if (
    recordCount > cumulativeRecordCount ||
    expiresAt <= now ||
    expiresAt > now + STOCKY_PORTABILITY_CURSOR_TTL_SECONDS + 60
  ) {
    throw localPortabilityIntegrityError("データセットの件数または期限を確認できません。");
  }
  return {
    blob: await boundedResponseBlob(
      response,
      MAX_STOCKY_DATASET_CHUNK_BYTES,
      contentSha256,
    ),
    filename: responseFilename(response, `ureyukibox-${dataset}-chunk.zip`),
    dataset,
    recordCount,
    cumulativeRecordCount,
    highWatermark: requiredPublicBigIntCursorHeader(
      response,
      "X-High-Watermark",
    ),
    partSequence: requiredPositiveIntegerHeader(response, "X-Part-Sequence"),
    expiresAt,
    nextCursor,
    completeCandidate: requiredFinalHeader(response),
    orderedRoot,
  };
}

export async function verifyStockyPortabilityDataset(
  dataset: string,
  verificationToken: string,
): Promise<StockyPortabilityDatasetVerification> {
  const result = await apiFetch<StockyPortabilityDatasetVerification>(
    `${PORTABILITY}/datasets/${encodeURIComponent(dataset)}/verify`,
    {
      method: "POST",
      body: { verification_token: verificationToken },
    },
  );
  let highWatermark: string;
  try {
    highWatermark = canonicalStockyPublicBigIntCursor(result.high_watermark);
  } catch {
    throw localPortabilityIntegrityError("データセットの最終検証結果を確認できません。");
  }
  if (
    result.dataset !== dataset ||
    !Number.isSafeInteger(result.record_count) ||
    result.record_count < 0 ||
    !/^[a-f0-9]{64}$/.test(result.ordered_root) ||
    result.verified !== true ||
    result.newer_ids_excluded !== true
  ) {
    throw localPortabilityIntegrityError("データセットの最終検証結果を確認できません。");
  }
  return { ...result, high_watermark: highWatermark };
}

export async function downloadStockySourceGenericRowsChunk(
  archive: Pick<StockySourceArchive, "sha256"> & {
    id: StockySourceArchiveId;
  },
  cursor: string | null,
): Promise<DownloadedGenericRowChunk> {
  const archiveId = requireStockySourceArchiveId(archive.id);
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  const response = await checkedRawFetch(
    `${PORTABILITY}/sources/${encodeStockySourceArchiveId(archiveId)}/generic-rows/chunks${query}`,
  );
  const nextCursor = response.headers.get("X-Next-Cursor") ?? "";
  const orderedRoot = response.headers.get("X-Ordered-Root") ?? "";
  const responseSourceSha256 = response.headers.get("X-Source-SHA256") ?? "";
  const contentSha256 = requiredContentSha256(response);
  if (
    response.headers.get("X-Source-Archive-ID") !== String(archiveId) ||
    responseSourceSha256 !== archive.sha256 ||
    nextCursor.length === 0 ||
    !/^[a-f0-9]{64}$/.test(orderedRoot)
  ) {
    throw localPortabilityIntegrityError("汎用行チャンクと原本の継続情報を確認できません。");
  }
  const recordCount = requiredIntegerHeader(response, "X-Record-Count");
  const cumulativeRecordCount = requiredIntegerHeader(
    response,
    "X-Cumulative-Record-Count",
  );
  const highWatermark = requiredIntegerHeader(response, "X-High-Watermark");
  const expiresAt = requiredPositiveIntegerHeader(
    response,
    "X-Cursor-Expires-At",
  );
  const now = Math.floor(Date.now() / 1000);
  if (
    recordCount > cumulativeRecordCount ||
    cumulativeRecordCount > highWatermark ||
    expiresAt <= now ||
    expiresAt > now + STOCKY_PORTABILITY_CURSOR_TTL_SECONDS + 60
  ) {
    throw localPortabilityIntegrityError("汎用行チャンクの件数を確認できません。");
  }
  return {
    blob: await boundedResponseBlob(
      response,
      MAX_STOCKY_GENERIC_CHUNK_BYTES,
      contentSha256,
    ),
    filename: responseFilename(
      response,
      `ureyukibox-stocky-generic-${archive.id}-chunk.zip`,
    ),
    archiveId: canonicalArchiveResponseId(archiveId),
    sourceSha256: responseSourceSha256,
    recordCount,
    cumulativeRecordCount,
    highWatermark,
    partSequence: requiredPositiveIntegerHeader(response, "X-Part-Sequence"),
    expiresAt,
    nextCursor,
    completeCandidate: requiredFinalHeader(response),
    orderedRoot,
  };
}

export async function verifyStockySourceGenericRows(
  archive: Pick<StockySourceArchive, "sha256"> & {
    id: StockySourceArchiveId;
  },
  verificationToken: string,
): Promise<StockyGenericRowVerification> {
  const archiveId = requireStockySourceArchiveId(archive.id);
  const result = await apiFetch<StockyGenericRowVerification>(
    `${PORTABILITY}/sources/${encodeStockySourceArchiveId(archiveId)}/generic-rows/verify`,
    {
      method: "POST",
      body: { verification_token: verificationToken },
    },
  );
  let responseArchiveId: string;
  try {
    responseArchiveId = canonicalStockyPublicBigIntId(result.archive_id);
  } catch {
    throw localPortabilityIntegrityError("汎用行の最終検証結果を確認できません。");
  }
  if (
    responseArchiveId !== String(archiveId) ||
    result.source_sha256 !== archive.sha256 ||
    result.verified !== true ||
    !Number.isSafeInteger(result.high_watermark) ||
    result.high_watermark < 0 ||
    !Number.isSafeInteger(result.record_count) ||
    result.record_count < 0 ||
    result.record_count !== result.high_watermark ||
    !Number.isSafeInteger(result.part_count) ||
    result.part_count < 1 ||
    !/^[a-f0-9]{64}$/.test(result.ordered_root)
  ) {
    throw localPortabilityIntegrityError("汎用行の最終検証結果を確認できません。");
  }
  return { ...result, archive_id: responseArchiveId };
}
